/**
 * 重写bridge sdk的方法，支持bridge方法调用次数统计，调用成功和失败次数上报，
 * 针对特殊需要统计回调值的bridge，自动上报成功的值
 */
import { BridgeTypeEnum, REPORT_TYPE, bridgeDetailParams } from '../index.interface'
import { installOnce } from './global-flag'
import { uid } from './uid'
/**
 * 鉴于每个bridge call的方法返回的结构不一致，这里只重点检测常用的bridge
 */

const bridgeCallFunctionMap = {
  popWebView: {
    isFail: (res: any) => {
      return res.status === 0
    }
  },
  navigate: {
    isFail: (res: any) => {
      return res.error === 0
    }
  },
  saveMediaToDeviceAlbum: {
    isFail: (res: any) => {
      return res.error === 1
    }
  },
  checkAppPermission: {
    isFail: (res: any) => {
      return res.status !== 1
    }
  },
  requestAppPermission: {
    isFail: (res: any) => {
      return res.status === 0
    },
    key: 'status'
  },
  getDeviceInfo: {
    isFail: (res: any) => {
      return res.error === 1
    }
  },
  getAppInfo: {
    isFail: (res: any) => {
      return !res.deviceID
    }
  },
  getLocation: {
    isFail: (res: any) => {
      return res.status === 0
    },
    key: 'status'
  },
  getTongdunBlackbox: {
    isFail: (res: any) => {
      return res.error === 1
    }
  },
  openICCamera2: {
    isFail: (res: any) => {
      return res.error === 1
    }
  },
  openICCamera3: {
    isFail: (res: any) => {
      return res.error === 1
    },
    key: 'error'
  },
  convertToBase64: {
    isFail: (res: any) => {
      return res.error === 2
    }
  },
  pickImage: {
    isFail: (res: any) => {
      return res.error === 1
    },
    key: 'error'
  },
  addLocalNotification: {
    isFail: (res: any) => {
      return res.error === 1
    }
  },
  requestAuthCodeFromCoreAuth: {
    isFail: (res: any) => {
      return res.errorCode !== 0
    }
  },
  getNetworkInfo: {
    isFail: (res: any) => {
      return !res.cellularType && !res.networkType
    }
  },
  startListenForOtp: {
    isFail: (res: any) => {
      return res.error === 1
    }
  },
  load: {
    isFail: (res: any) => {
      return res.status === 0
    }
  },
  save: {
    isFail: (res: any) => {
      return res.status === 0
    }
  },
  pickSystemContact: {
    isFail: (res: any) => {
      return res.error === 1
    },
    key: 'error'
  },
  uploadDeviceInfo: {
    isFail: (res: any) => {
      return res.error === 1
    }
  }
}

// 单独上报统计bridge的结果的bridgename，比如getLocation，不同的值具有统计意义
const bridgeNameResultKeys = [
  'getLocation',
  'openICCamera3',
  'pickSystemContact',
  'pickImage',
  'requestAppPermission'
]

/**
 * 所有 CustomBrigde 实例共享同一层 bridgeCallHandler Hook，通过 `_bridgeInstances`
 * 列表分发数据。跨 module 实例去重由 `installOnce` 负责，详见 `util/global-flag.ts`。
 */
const _bridgeInstances: CustomBrigde[] = []

export default class CustomBrigde {
  updateEventList // 记录bridge调用情况
  cacheBridge // 缓存bridge
  deleteBridge // 删除bridge
  getCacheBridge // 获取bridge

  constructor({ updateEventList, cacheBridge, deleteBridge, getCacheBridge }) {
    this.updateEventList = updateEventList
    this.cacheBridge = cacheBridge
    this.deleteBridge = deleteBridge
    this.getCacheBridge = getCacheBridge

    _bridgeInstances.push(this)
    installOnce("bridge-hooked", () => this.init())
  }

  init() {
    this.reWriteBridgeCallHandler()
  }

  reWriteBridgeCallHandler() {
    const _func = window.bridgeCallHandler
    if (_func) {
      window.bridgeCallHandler = (...params) => {
        const [name, options, resolveFun] = params

        const startTime = Date.now()

        if (name === 'getAppInfo' && this.isGetAppInfoCalledBySelf(resolveFun)) {
          _func(...params)
          return
        }

        // 为每个实例生成独立 uuid 并缓存 bridge 调用
        const instanceUuids: Array<{ instance: CustomBrigde; uuid: string }> = []
        _bridgeInstances.forEach((instance) => {
          const id = uid()
          instanceUuids.push({ instance, uuid: id })
          instance.cacheBridge(id, {
            type: REPORT_TYPE.Bridge,
            data: {
              name,
              type: BridgeTypeEnum.total
            }
          })
        })

        _func.call(window, name, options, (...res) => {
          const isExist = Object.keys(bridgeCallFunctionMap).includes(name)
          const isInSuccessKey = bridgeNameResultKeys.includes(name)
          const duration = Date.now() - startTime

          if (isExist) {
            const isFail = bridgeCallFunctionMap[name].isFail(...res)
            // 广播给所有已注册的实例
            instanceUuids.forEach(({ instance, uuid }) => {
              if (isInSuccessKey && !isFail) {
                const data = res[0] || {}
                instance.reportBridgeDetail(
                  {
                    name,
                    isSuccess: !isFail,
                    duration,
                    successResult: data[bridgeCallFunctionMap[name].key]
                  },
                  uuid
                )
              } else {
                instance.reportBridgeDetail(
                  {
                    name,
                    isSuccess: !isFail,
                    duration
                  },
                  uuid
                )
              }
            })
          }
          resolveFun(...res)
        })
      }
    }
  }

  // 判断getAppInfo的回调函数是不是和bridge sdk调用的回调函数一致
  isGetAppInfoCalledBySelf(resolveFun) {
    return resolveFun.toString().includes('BI_TRACKING_PROTO_GENERAL_INFO.deviceid')
  }

  reportBridge(bridgeName: string) {
    this.updateEventList({
      type: REPORT_TYPE.Bridge,
      data: {
        name: bridgeName,
        type: BridgeTypeEnum.total
      }
    })
  }

  reportBridgeDetail(params: bridgeDetailParams, uuid) {
    this.updateEventList({
      type: REPORT_TYPE.Bridge,
      data: {
        ...params,
        type: BridgeTypeEnum.detail
      }
    })
    this.updateEventList({
      ...this.getCacheBridge(uuid)
    })
    this.deleteBridge(uuid)
  }
}
